import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { useState } from "react";

const InlineInput = ({
  value,
  isLoading,
  onSubmit,
  onBlur,
  onCancel,
  inputStyle,
}: {
  value: string;
  isLoading?: boolean;
  onSubmit?: (value: string) => void;
  onBlur?: (value: string) => void;
  onCancel?: () => void;
  inputStyle?: string;
}): JSX.Element => {
  const [inputValue, setInputValue] = useState(value);

  // Handle input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(event.target.value);
  };

  // Submit and pass the new value
  const handleSubmit = (): void => {
    onSubmit && onSubmit(inputValue);
  };

  // Execute prop function on blur with the new value
  const handleBlur = (): void => {
    onBlur && onBlur(inputValue);
  };

  // Cancel and revert to the original value
  const handleCancel = (): void => {
    setInputValue(value); // Revert to original value
    onCancel && onCancel();
  };

  return (
    <div className="flex items-center space-x-2">
      <input
        type="text"
        className={classNames(
          "block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
          inputStyle ?? ""
        )}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isLoading}
      />

      {onSubmit ? (
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-2 py-2 text-sm text-white shadow-sm duration-200 ease-in-out hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          disabled={isLoading}>
          {isLoading ? (
            <Spinner loading={isLoading} />
          ) : (
            <CheckIcon className="h-5 w-5 stroke-2" aria-hidden="true" />
          )}
        </button>
      ) : null}

      {onCancel ? (
        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex items-center gap-x-1.5 rounded-md bg-red-500 px-2 py-2 text-sm text-white shadow-sm duration-200 ease-in-out hover:bg-red-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
          <XMarkIcon className="h-5 w-5 stroke-2" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

export default InlineInput;
